import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { MyButton, MyContextMenu, MyPopper, MyChip } from '@interstellar-component'
import { ChevronDown, File06 } from '@untitled-ui/icons-react'
import { enquiryStatusLabel } from '@services/enquiryStatus'

// Backend ships `enquiry_history` inside every workforce-detail response:
//   [{ id, reference_number, status, category, created_at, completed_at }]
// ordered created_at DESC. Selecting an entry adds `?enquiry=<id>` to the
// current URL; selecting "Latest enquiry" strips the query param. Both the
// Report tab and the Risk Signals page read the same param.
//
// The dropdown does not render when fewer than 2 enquiries exist — there's
// nothing to switch between.

// The switcher feed ships `status` AND `quota_hold_reason` (workforce.repositories
// #getEnquiryHistory) precisely so this chip can tell a request waiting on the
// customer's money from one waiting on us — see @services/enquiryStatus.

const STATUS_CHIP_COLOR = {
  completed: 'success',
  awaiting_form: 'warning',
  verification: 'warning',
  form_revision: 'warning',
  sent_to_clik: 'brand',
  quota_hold: 'warning',
  failed: 'error',
  canceled: 'modern',
}

function formatShortDate(value) {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

export default function EnquirySwitcher({ enquiryHistory = [] }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedId = searchParams.get('enquiry') || null

  const items = useMemo(
    () => (Array.isArray(enquiryHistory) ? enquiryHistory : []),
    [enquiryHistory]
  )

  // Single-enquiry workforces have nothing to switch between — hide the
  // control entirely so the header stays uncluttered.
  if (items.length < 2) return null

  const selected = selectedId ? items.find((e) => String(e.id) === String(selectedId)) : null
  const buttonLabel = selected
    ? `${selected.reference_number ?? '—'} · ${formatShortDate(selected.created_at)}`
    : `Latest enquiry · ${items[0]?.reference_number ?? ''}`

  const select = (enquiryId) => {
    const next = new URLSearchParams(searchParams)
    if (enquiryId) next.set('enquiry', enquiryId)
    else next.delete('enquiry')
    setSearchParams(next, { replace: false })
  }

  const menuItems = [
    {
      label: 'Latest enquiry',
      onClick: () => select(null),
    },
    ...items.map((e) => ({
      label: (
        <div className="flex w-full items-center justify-between gap-3 min-w-0">
          <div className="flex flex-col min-w-0">
            <span className="text-sm-medium text-gray/900 truncate">
              {e.reference_number ?? '—'}
            </span>
            <span className="text-xs-regular text-gray/500 truncate">
              {formatShortDate(e.created_at)}
            </span>
          </div>
          <MyChip
            label={enquiryStatusLabel(e) || '—'}
            color={STATUS_CHIP_COLOR[e.status] ?? 'modern'}
            variant="modern"
            size="sm"
            rounded="lg"
          />
        </div>
      ),
      onClick: () => select(e.id),
    })),
  ]

  return (
    <MyPopper
      placement="bottom-end"
      target={(open, show) => (
        <MyButton
          onClick={show}
          color="secondary"
          variant="outlined"
          size="md"
          customClassname="gap-2"
        >
          <File06 className="h-5 w-5 text-gray-500" />
          {buttonLabel}
          <ChevronDown className={`h-5 w-5 transition-transform ${open ? 'rotate-180' : ''}`} />
        </MyButton>
      )}
    >
      {(_open, anchorEl, _show, close) => (
        <div style={{ width: anchorEl?.offsetWidth }}>
          <MyContextMenu
            className="!w-full"
            menuButtonGroups={[
              menuItems.map((item) => ({
                ...item,
                onClick: () => {
                  item.onClick()
                  close()
                },
              })),
            ]}
          />
        </div>
      )}
    </MyPopper>
  )
}
